import React, { useState, useRef, useContext } from "react";
import {
  Box,
  Hidden,
  Divider,
  FlexBox,
  GridContainer,
  Row,
  Column,
  Typography
} from "@vp/swan";
import { useTranslation } from "../useTranslation";

import SignInLayout from "./SignIn/SignIn";
import SignUpLayout from "./SignUp/SignUp";
import ResetScreen from "./Reset/Reset";
import combinedLogo from "./Logo-combined.svg";
import vcsLogo from "./vcs_logo.svg";
import { useNotificationContext } from "../NotificationContext";
import Notification from "./Components/Notification";
import {
  Brand,
  useBrand,
  useSupportPhoneNumber,
  useWauth
} from "@monorepo/components";
import { localesWithDisabledSocialLogin } from "../disabledSocialLogin";
import { localesWithDisabledSignUp } from "../disabledSignUp";

import "./ScreensLayout.scss";
import { SITE_VCS, UNIFICATION_ERROR_MESSAGE, VISTAPRINT_ONLY_BRAND } from "../constant";
import MultipleAccounts from "./Unify/MultipleAccounts";
import ContactFooter from "./Components/ContactFooter";
import { SignAuth0Context } from "../Auth0Context";
import useCustomization from "./useCustomization";
import VCSFooter from "./Components/VCSFooter";

export enum Screens {
  SignIn,
  SignUp,
  Reset,
  Unify
}

const ScreensLayout = () => {
  const [t] = useTranslation("app");
  const [isLogoLoaded, setIsLogoLoaded] = useState(false);

  const { clearNotification, notification } = useNotificationContext();
  const wauth = useWauth();
  const errorDetails = (wauth.loginFailed || "").split("~");
  const [screen, setScreen] = useState(
    (errorDetails[0] === UNIFICATION_ERROR_MESSAGE ? Screens.Unify : ((wauth.navHint === "signUp" && !wauth.restrictSignup) ? Screens.SignUp : Screens.SignIn))
  );
  const [isGoogleOneTapInitiated, setIsGoogleOneTapInitiated] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const brand = useBrand();
  const tenant = Brand[brand];
  const culture = document.documentElement.lang;
  const channelProviderUrl = `${process.env.REACT_APP_CARE_CHANNEL_PROVIDER_URL}/phone?tenant=${tenant}&locale=${culture}&requestor=LoginUI`;
  const supportPhoneNumber = useSupportPhoneNumber(
    channelProviderUrl,
    t("header.support.text")
  );
  const auth0 = useContext(SignAuth0Context);
  const site = wauth.site || "vistaprint";
  const isSocialLoginDisabled = (locale: string): boolean => {
    if (VISTAPRINT_ONLY_BRAND.includes(site.toLowerCase())) { return (localesWithDisabledSocialLogin[tenant] || []).includes(locale); }
    return false;
  };

  const isSignUpDisabled = (locale: string): boolean => {
    if (VISTAPRINT_ONLY_BRAND.includes(site.toLowerCase())) { return (localesWithDisabledSignUp[tenant] || []).includes(locale); }
    return false;
  };

  const { isAutomaticAgreementEnabled } = useCustomization(site);

  const renderApp = () => {
    if (screen === Screens.Reset) {
      return <ResetScreen onChangeScreen={onChangeScreen} />;
    }
    if (wauth?.enforcedEmail && wauth?.enableGoogleOneTap && !wauth.loginFailed) {
      if (!isGoogleOneTapInitiated) {
        setIsGoogleOneTapInitiated(true);
        auth0.authenticateGoogle(wauth.enforcedEmail);
      }
      return (

        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)"
        }}>
          <Typography textAlign="center" fontSkin="body-standard-bold"><h1>{t("app:loadingText")}</h1></Typography>
        </div>

      );
    }

    // layout for medium and large screens
    switch (screen) {
      case Screens.SignUp:
        return (
          <SignUpLayout
            wauth={wauth}
            onChangeScreen={onChangeScreen}
            notificationRef={notificationRef}
            isSocialLoginDisabled={isSocialLoginDisabled}
            isAutomaticAgreementEnabled={isAutomaticAgreementEnabled}
          />
        );
      case Screens.Unify:
        return (
          <MultipleAccounts
            notificationRef={notificationRef}
            unificationDetail={errorDetails[1]}
          />
        );
      case Screens.SignIn:
      default:
        return (
          <SignInLayout
            onChangeScreen={onChangeScreen}
            notificationRef={notificationRef}
            isSocialLoginDisabled={isSocialLoginDisabled}
            isSignUpLinkHidden={isSignUpDisabled}
            isAutomaticAgreementEnabled={isAutomaticAgreementEnabled}
          />
        );
    }
  };

  const onChangeScreen = (newScreen: Screens): void => {
    if (screen !== newScreen) {
      clearNotification();
      setScreen(newScreen);
    }
  };

  const MAX_WIDTH = { maxWidth: "505px" };
  const MAX_HEIGHT = { maxHeight: "66px" };

  const vistaHeader = () => {
    if (wauth.site === SITE_VCS) {
      return (
        <FlexBox justifyContent="center" alignItems="center" padding={3} >
          <img
            className="vcs-logo"
            data-testid="vcs-logo"
            src={wauth.vcsStoreLogoUrl || vcsLogo}
            alt={t("vcsLogoAlt")}
          />
        </FlexBox>);
    }
    return (<>
      <Hidden xs>
        <GridContainer>
          <Row>
            <Column
              style={{ ...MAX_WIDTH }}
              span={5}
              spanMd={10}
              spanSm={10}
              mt={4}
              mb={4}
              className="grid-column"
              marginX="auto">
              <Row>
                <img
                  onLoad={(e) => {
                    setIsLogoLoaded(true);
                  }}
                  className={isLogoLoaded ? "vista-logo" : "vista-logo-hide"}
                  data-testid="vista-logo"
                  src={combinedLogo}
                  alt={t("vistaprintLogoAlt")}
                />
              </Row>
            </Column>
          </Row>
        </GridContainer>
      </Hidden>
      <Hidden xl lg md sm>
        <Box
          style={{ ...MAX_HEIGHT }}
          className="grid-column vista-logo"
          marginX={4}
          mt={5}
          mb={5}>
          <img
            onLoad={(e) => {
              setIsLogoLoaded(true);
            }}
            className={isLogoLoaded ? "" : "vista-logo-hide"}
            data-testid="vista-logo"
            src={combinedLogo}
            alt={t("vistaprintLogoAlt")}
          />
        </Box>
      </Hidden>
    </>);
  };
  return (
    <FlexBox
      justifyContent="space-between"
      flexWrap="nowrap"
      flexDirection="column"
      className="screen-layout"
    >
      <Box>
        {vistaHeader()}
        { isLogoLoaded && <Divider />}
        <Box ref={notificationRef} my="between-sections">
          <Notification notification={notification} clearNotification={clearNotification} />
          {renderApp()}
        </Box>
      </Box>
      {wauth.site === SITE_VCS && <VCSFooter />}
      <ContactFooter supportPhoneNumber={supportPhoneNumber} />
    </FlexBox>
  );
};

export default ScreensLayout;
